import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight, Leaf, Mountain, Sparkles, Sun, Users, Waves,
    Network, Shield, Globe2, PartyPopper, Star,
} from "lucide-react";
import { fetchProject, fetchStats, fetchThemes, fetchContributions } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import EtwinningBadge from "@/components/EtwinningBadge";
import LiveStatsWidget from "@/components/LiveStatsWidget";
import ContributionCard from "@/components/ContributionCard";
import CountryFlag from "@/components/CountryFlag";
import { useT } from "@/lib/i18n";

const THEME_ICONS = {
    "mountain-day": Mountain,
    "green-to-blue": Waves,
    "renewable-energy": Sun,
    "new-year-wishes": Sparkles,
    introductions: Users,
    "january-mission": Network,
    "safer-internet": Shield,
    "april-earth": Globe2,
    "may-etwinning": PartyPopper,
};

const BENTO_SPAN = {
    0: "md:col-span-7 md:row-span-2 min-h-[420px]",
    1: "md:col-span-5 min-h-[200px]",
    2: "md:col-span-5 min-h-[200px]",
    3: "md:col-span-6 min-h-[260px]",
    4: "md:col-span-6 min-h-[260px]",
    5: "md:col-span-4 min-h-[240px]",
    6: "md:col-span-4 min-h-[240px]",
    7: "md:col-span-4 min-h-[240px]",
    8: "md:col-span-12 min-h-[220px]",
};

const FOUNDER_COUNTRIES = ["Türkiye", "Romania"];
const PARTNER_COUNTRIES = [
    "Italy",
    "Republic of Moldova",
    "Georgia",
    "Lithuania",
    "Bosnia and Herzegovina",
];

export default function HomePage() {
    const { t } = useT();
    const [project, setProject] = useState(null);
    const [themes, setThemes] = useState([]);
    const [stats, setStats] = useState(null);
    const [mayFeatured, setMayFeatured] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [p, t, s, may] = await Promise.all([
                    fetchProject(),
                    fetchThemes(),
                    fetchStats(),
                    fetchContributions({ theme: "may-etwinning", limit: 4 }),
                ]);
                if (cancelled) return;
                setProject(p);
                setThemes(t);
                setStats(s);
                setMayFeatured(may);
            } catch (e) {
                /* noop */
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    // Reorder themes: pin may-etwinning first so the most important month is the hero of the bento.
    const orderedThemes = [...themes].sort((a, b) => {
        if (a.slug === "may-etwinning") return -1;
        if (b.slug === "may-etwinning") return 1;
        return 0;
    });

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden">
                {project?.hero_image && (
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url(${project.hero_image})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    />
                )}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(31,69,48,0.80) 0%, rgba(15,46,69,0.82) 100%)",
                    }}
                />
                <div className="container-pad relative py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10 text-white">
                    <div className="md:col-span-10">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="flex flex-wrap items-center gap-3 mb-8"
                        >
                            <EtwinningBadge href={project?.etwinning_url} variant="dark" />
                            <span className="hidden sm:inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.25em] font-semibold opacity-90">
                                <Leaf size={14} strokeWidth={1.5} />
                                {t("home.badge")}
                                <Waves size={14} strokeWidth={1.5} />
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className="heading-serif text-6xl md:text-7xl lg:text-[6rem] leading-[0.95] tracking-tight text-balance"
                        >
                            {t("home.heroIntroPrefix")}
                            <br />
                            <em className="text-white/75 font-normal italic">
                                {t("home.heroSubtitle")}
                            </em>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 text-xl md:text-2xl text-white/90 max-w-3xl leading-relaxed"
                        >
                            {project?.project_intro}
                        </motion.p>

                        {/* Founding + partner flags strip — with explicit co-founder school names */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl"
                            data-testid="founder-flag-strip"
                        >
                            <div className="rounded-2xl bg-white/12 backdrop-blur-md border border-white/20 px-5 py-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <CountryFlag country="Türkiye" size="lg" />
                                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/75">
                                        Co-founder · Türkiye
                                    </span>
                                </div>
                                <p className="text-white text-[15px] font-medium leading-snug">
                                    Istanbul TOKİ Kayaşehir MTAL
                                </p>
                            </div>
                            <div className="rounded-2xl bg-white/12 backdrop-blur-md border border-white/20 px-5 py-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <CountryFlag country="Romania" size="lg" />
                                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/75">
                                        Co-founder · România
                                    </span>
                                </div>
                                <p className="text-white text-[15px] font-medium leading-snug">
                                    Liceul Teoretic „Aurel Vlaicu&quot;, Breaza (LTAV)
                                </p>
                            </div>
                            <div className="md:col-span-2 rounded-full bg-white/8 backdrop-blur-md border border-white/15 px-5 py-2.5 flex flex-wrap items-center gap-4">
                                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/70">
                                    Partner schools
                                </span>
                                {PARTNER_COUNTRIES.map((c) => (
                                    <span key={c} className="inline-flex items-center gap-1.5 text-white/90 text-sm">
                                        <CountryFlag country={c} size="md" />
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-10 flex flex-wrap gap-4"
                        >
                            <Link
                                to="/theme/may-etwinning"
                                data-testid={HOME.heroCta}
                                className="inline-flex items-center gap-2 bg-brand-gold text-ink-primary px-7 py-4 rounded-full font-semibold text-[15px] hover:bg-white transition-colors duration-300"
                            >
                                <Star size={16} strokeWidth={1.8} fill="currentColor" />
                                {t("common.featured")} → eTwinning Day
                                <ArrowUpRight size={16} strokeWidth={1.6} />
                            </Link>
                            <a
                                href="#themes"
                                data-testid={HOME.heroExplore}
                                className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-4 rounded-full font-medium text-[15px] hover:bg-white/10 transition-colors duration-300"
                            >
                                {t("common.seeAllThemes")}
                            </a>
                        </motion.div>

                        {/* Live engagement stats */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-10"
                        >
                            <LiveStatsWidget variant="dark" />
                        </motion.div>
                    </div>
                </div>

                {stats && (
                    <div className="relative border-t border-white/15 bg-black/20 backdrop-blur-sm">
                        <div
                            data-testid={HOME.statsBlock}
                            className="container-pad py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-white"
                        >
                            <Stat label={t("home.statsContributions")} value={stats.total_contributions} />
                            <Stat label={t("home.statsSchools")} value={stats.total_schools} />
                            <Stat label={t("home.statsCountries")} value={stats.total_countries} />
                            <Stat label={t("home.statsThemes")} value={stats.total_themes} />
                        </div>
                    </div>
                )}
            </section>

            {/* MAY FEATURED RIBBON */}
            {mayFeatured.length > 0 && (
                <section className="bg-gradient-to-br from-brand-gold/10 via-bg-base to-brand-blue/5 border-y border-brand-gold/20">
                    <div className="container-pad py-20 md:py-28">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-end">
                            <div className="md:col-span-8">
                                <div className="caption text-brand-gold flex items-center gap-2 mb-3">
                                    <Star size={12} strokeWidth={2} fill="currentColor" />
                                    {t("common.mostImportantMonth")} · May
                                </div>
                                <h2 className="heading-serif text-5xl md:text-6xl text-ink-primary leading-[0.95] tracking-tight text-balance">
                                    eTwinning Day — <em className="gradient-g2b-text">the year&apos;s closing chord.</em>
                                </h2>
                            </div>
                            <div className="md:col-span-4 md:text-right">
                                <Link
                                    to="/theme/may-etwinning"
                                    data-testid="may-featured-cta"
                                    className="btn-primary"
                                >
                                    {t("common.seeAll")} <ArrowUpRight size={15} strokeWidth={1.6} />
                                </Link>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {mayFeatured.slice(0, 3).map((c, i) => (
                                <ContributionCard
                                    key={c.id}
                                    contribution={{ ...c, accent: "gold" }}
                                    index={i}
                                />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ALL THEMES BENTO */}
            <section id="themes" className="container-pad py-24 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-14">
                    <div className="md:col-span-5">
                        <div className="caption text-brand-green mb-3">{t("home.themesEyebrow")}</div>
                        <h2 className="heading-serif text-5xl md:text-6xl text-ink-primary leading-tight text-balance">
                            {t("home.themesHeading1")}
                            <br />
                            <em className="gradient-g2b-text">{t("home.themesHeading2")}</em>
                        </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 text-ink-secondary leading-relaxed text-lg md:text-xl self-end">
                        {t("home.themesBody")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-min gap-6">
                    {orderedThemes.map((theme, i) => {
                        const Icon = THEME_ICONS[theme.slug] || Leaf;
                        const span = BENTO_SPAN[i] || "md:col-span-6 min-h-[240px]";
                        const isMay = theme.slug === "may-etwinning";
                        return (
                            <motion.div
                                key={theme.slug}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                className={span}
                            >
                                <Link
                                    to={`/theme/${theme.slug}`}
                                    data-testid={HOME.themeCard(theme.slug)}
                                    className="group relative block h-full rounded-2xl overflow-hidden border border-black/5 bg-bg-card"
                                >
                                    <div
                                        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                        style={{ backgroundImage: `url(${theme.image_url})` }}
                                    />
                                    <div
                                        className="absolute inset-0"
                                        style={{
                                            background: isMay
                                                ? "linear-gradient(180deg, rgba(229,169,61,0.08) 0%, rgba(15,46,69,0.88) 100%)"
                                                : i === 0
                                                ? "linear-gradient(180deg, rgba(31,69,48,0.15) 0%, rgba(15,46,69,0.85) 100%)"
                                                : "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(15,46,69,0.78) 100%)",
                                        }}
                                    />
                                    <div className="relative h-full flex flex-col justify-between p-8 text-white">
                                        <div className="flex items-center justify-between">
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md text-[11px] uppercase tracking-[0.2em]">
                                                <Icon size={13} strokeWidth={1.5} />
                                                {theme.contribution_count} {t("common.entries")}
                                            </span>
                                            {isMay && (
                                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-gold text-ink-primary text-[11px] uppercase tracking-[0.2em] font-semibold">
                                                    <Star size={11} strokeWidth={2} fill="currentColor" />
                                                    {t("common.featured")}
                                                </span>
                                            )}
                                            {!isMay && (
                                                <ArrowUpRight
                                                    className="opacity-70 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                                    size={20}
                                                    strokeWidth={1.5}
                                                />
                                            )}
                                        </div>
                                        <div>
                                            <div className="caption text-white/70 mb-2">{theme.subtitle}</div>
                                            <h3 className="heading-serif text-3xl md:text-4xl leading-tight text-balance">
                                                {theme.title}
                                            </h3>
                                            {i === 0 && (
                                                <p className="mt-4 text-white/85 max-w-md leading-relaxed text-base">
                                                    {theme.tagline}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="bg-bg-alt/50 border-y border-black/5">
                <div className="container-pad py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-3">
                        <div className="caption text-brand-blue">{t("home.manifestoEyebrow")}</div>
                    </div>
                    <blockquote className="md:col-span-9 heading-serif text-3xl md:text-5xl text-ink-primary leading-[1.15] tracking-tight text-balance">
                        {t("home.manifestoQuote")}
                        <footer className="mt-8 text-base md:text-lg font-sans not-italic text-ink-muted">
                            {t("home.manifestoSource")}
                        </footer>
                    </blockquote>
                </div>
            </section>

            {/* SCHOOLS CTA */}
            <section className="container-pad py-24 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    <div className="md:col-span-7">
                        <div className="caption text-brand-green mb-3">{t("home.schoolsEyebrow")}</div>
                        <h2 className="heading-serif text-4xl md:text-5xl text-ink-primary leading-tight text-balance">
                            {t("home.schoolsHeading1")}{" "}
                            <em className="gradient-g2b-text">{t("home.schoolsHeading2")}</em>
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <Link to="/schools" className="btn-primary" data-testid="home-schools-cta">
                            {t("common.browseSchools")}
                            <ArrowUpRight size={16} strokeWidth={1.6} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="heading-serif text-4xl md:text-5xl">{value}</div>
            <div className="text-[12px] uppercase tracking-[0.2em] text-white/80 mt-1 font-medium">
                {label}
            </div>
        </div>
    );
}
